import { useState } from 'react'
import { Link } from '@inertiajs/react'
import { FileText, Link2, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import { taskStatusTone } from '@/frontend/features/Fde/projectTaskPresentation'
import type { FdeCopy } from '@/frontend/features/Fde/types'
import type { ProjectTaskContext, ProjectTaskLinkOptions } from '@/frontend/features/Fde/projectTaskTypes'

interface Props {
  projectId: string
  context: ProjectTaskContext
  options: ProjectTaskLinkOptions
  canManage: boolean
  disabled: boolean
  copy: FdeCopy
  onChange: (field: 'related_task_ids' | 'repo_paths', values: string[]) => void
}

export default function ProjectTaskLinks({
  projectId,
  context,
  options,
  canManage,
  disabled,
  copy,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false)
  const [kind, setKind] = useState<'tasks' | 'files'>('tasks')
  const [query, setQuery] = useState('')
  const relatedIds = context.related_tasks.map((task) => task.id)
  const normalized = query.trim().toLocaleLowerCase('pt-BR')
  const candidates =
    kind === 'tasks'
      ? options.tasks
          .filter((task) => !relatedIds.includes(task.id))
          .map((task) => ({
            value: task.id,
            title: `${task.identifier} · ${task.title}`,
            status: task.status,
          }))
      : options.repo_paths
          .filter((path) => !context.repo_paths.includes(path))
          .map((path) => ({ value: path, title: path, status: undefined }))
  const filtered = candidates.filter((item) => item.title.toLocaleLowerCase('pt-BR').includes(normalized))
  const hasLinks = relatedIds.length > 0 || context.repo_paths.length > 0
  if (!hasLinks && !canManage) return null

  function add(value: string) {
    onChange(
      kind === 'tasks' ? 'related_task_ids' : 'repo_paths',
      kind === 'tasks' ? [...relatedIds, value] : [...context.repo_paths, value],
    )
    setOpen(false)
    setQuery('')
  }

  return (
    <section className="mb-7 mt-6 border-t border-border/60 pt-4" aria-label={copy.management.task_links}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-xs font-medium text-muted-foreground">{copy.management.task_links}</h3>
        {canManage && (
          <Popover
            open={open}
            onOpenChange={(next) => {
              setOpen(next)
              setQuery('')
            }}
          >
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" disabled={disabled} className="h-7 px-2 text-xs">
                <Plus className="size-3.5" />
                {copy.management.link_task_context}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-2">
              <div className="mb-2 flex gap-1" role="group" aria-label={copy.management.task_link_kind}>
                {(['tasks', 'files'] as const).map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={kind === value}
                    onClick={() => setKind(value)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-xs',
                      kind === value ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted/50',
                    )}
                  >
                    {value === 'tasks' ? copy.management.tasks : copy.management.task_link_files}
                  </button>
                ))}
              </div>
              <label className="relative block">
                <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  aria-label={copy.management.search_task_links}
                  placeholder={copy.management.search_task_links}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="h-8 pl-8 text-xs"
                />
              </label>
              <div className="mt-2 max-h-56 overflow-y-auto">
                {filtered.map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    disabled={disabled}
                    onClick={() => add(item.value)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-xs hover:bg-muted disabled:opacity-50"
                    title={item.title}
                  >
                    {item.status ? (
                      <span className={cn('size-2 shrink-0 rounded-full', taskStatusTone[item.status])} />
                    ) : (
                      <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className="min-w-0 flex-1 truncate">{item.title}</span>
                  </button>
                ))}
                {filtered.length === 0 && (
                  <p className="px-2 py-4 text-xs text-muted-foreground">
                    {copy.management.no_task_links_found}
                  </p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
      <div className="space-y-0.5">
        {context.related_tasks.map((task) => (
          <div key={task.id} className="group flex items-center gap-2 rounded-md hover:bg-muted/30">
            <Link
              href={fdeProjectPath(`${fdeRoutes.projectTasks}/${task.id}`, projectId)}
              className="flex min-w-0 flex-1 items-center gap-2 py-2 text-xs"
            >
              <Link2 className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="shrink-0 text-[10px] text-muted-foreground">{task.identifier}</span>
              <span className="min-w-0 flex-1 truncate">{task.title}</span>
              <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[10px]', taskStatusTone[task.status])}>
                {copy.management.statuses[task.status]}
              </span>
            </Link>
            {canManage && (
              <Button
                size="icon-sm"
                variant="ghost"
                disabled={disabled}
                aria-label={copy.management.unlink_task_context.replace('%{name}', task.title)}
                onClick={() =>
                  onChange(
                    'related_task_ids',
                    relatedIds.filter((id) => id !== task.id),
                  )
                }
                className="size-6 text-muted-foreground opacity-50 hover:opacity-100 group-focus-within:opacity-100"
              >
                <X className="size-3" />
              </Button>
            )}
          </div>
        ))}
        {context.repo_paths.map((path) => (
          <div key={path} className="group flex items-center gap-2 rounded-md hover:bg-muted/30">
            <Link
              href={fdeProjectPath(fdeRoutes.material, projectId, { file: path })}
              title={path}
              className="flex min-w-0 flex-1 items-center gap-2 py-2 text-xs"
            >
              <FileText className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{path}</span>
            </Link>
            {canManage && (
              <Button
                size="icon-sm"
                variant="ghost"
                disabled={disabled}
                aria-label={copy.management.unlink_task_context.replace('%{name}', path)}
                onClick={() =>
                  onChange(
                    'repo_paths',
                    context.repo_paths.filter((value) => value !== path),
                  )
                }
                className="size-6 text-muted-foreground opacity-50 hover:opacity-100 group-focus-within:opacity-100"
              >
                <X className="size-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}
